import React, { useState } from 'react';

import {
  Stack,
  Table,
  Tbody,
  Thead,
  Tr,
  Th,
  Heading,
  TableContainer,
  ButtonGroup,
  IconButton,
  Box,
  useColorModeValue,
  Td,
  Alert,
  AlertDescription,
  AlertIcon,
  Tooltip,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { MdBarChart, MdOutlineTableRows } from 'react-icons/md';

import { DirectContribution } from 'hooks/data/datasets/directContribution/types';
import { LciaMethod } from 'hooks/data/datasets/lcia/types';
import { useDatasetSession } from 'pages/Dataset/DatasetSessionContext';
import DirectContributionRow from 'pages/ImpactAssessment/components/DirectContribution/DirectContributionRow';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import DirectContributionBarChart from './DirectContributionBarChart';

type Props = {
  exchanges: DirectContribution[];
  unitName: string;
  indicatorId: number;
  category: string;
  indicatorName: string;
  method: LciaMethod;
};

const DirectContributionList = ({ method, category, indicatorName, exchanges, unitName, indicatorId }: Props) => {
  const [view, setView] = useState<'list' | 'graph'>('list');
  const { increment } = useDatasetSession();
  const iconButtonActiveColor = useColorModeValue('white', 'whiteAlpha.300');
  const iconButtonBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const iconButtonHoverBg = useColorModeValue('gray.200', 'gray.600');
  const iconFillActive = useColorModeValue('gray.600', 'white');
  const iconFillInactive = '#AEACAC';
  const bgColor = useColorModeValue('white', 'gray.700');
  const iconButtonBorderColor = useColorModeValue('gray.300', 'whiteAlpha.400');

  const isListView = view === 'list';
  const hasProductColumn = exchanges.some((item) => item.type === 'product');
  const columnCount = hasProductColumn ? 4 : 3;

  return (
    <>
      {exchanges.length === 0 ? (
        <Stack spacing={4} bg={bgColor} p={2} borderRadius="md">
          <Alert variant="info">
            <AlertIcon />
            <AlertDescription>No direct contributors found</AlertDescription>
          </Alert>
        </Stack>
      ) : (
        <Stack spacing={4} bg={bgColor} p={5} borderRadius="md">
          <Stack justifyContent="space-between" alignItems="center" direction="row">
            <Heading as={'h5'} fontWeight={'semibold'} fontSize={'md'}>
              Contributors to impact score ({exchanges.length})
            </Heading>
            <ButtonGroup size="sm" isAttached border="1px solid" borderColor={iconButtonBorderColor} borderRadius="md">
              <Tooltip hasArrow label="Table" placement="top">
                <IconButton
                  aria-label="List view"
                  variant="subtle"
                  bg={isListView ? iconButtonActiveColor : iconButtonBg}
                  borderRadius="md"
                  _hover={{ bg: iconButtonHoverBg }}
                  _active={{ bg: iconButtonActiveColor }}
                  icon={<MdOutlineTableRows size={16} fill={isListView ? iconFillActive : iconFillInactive} />}
                  onClick={() => {
                    setView('list');
                    mixpanel.track(MIXPANEL_EVENTS.IMPACT_ASSESSMENT_TABLE_VIEW_SELECTED);
                    increment('impact_assessment_action_count');
                  }}
                />
              </Tooltip>
              <Tooltip hasArrow label="Graph" placement="top">
                <IconButton
                  aria-label="Graph View"
                  variant="subtle"
                  bg={!isListView ? iconButtonActiveColor : iconButtonBg}
                  borderRadius="md"
                  _hover={{ bg: iconButtonHoverBg }}
                  _active={{ bg: iconButtonActiveColor }}
                  icon={
                    <Box transform="rotate(90deg)">
                      <MdBarChart size={16} fill={!isListView ? iconFillActive : iconFillInactive} />
                    </Box>
                  }
                  onClick={() => {
                    setView('graph');
                    mixpanel.track(MIXPANEL_EVENTS.IMPACT_ASSESSMENT_GRAPH_VIEW_SELECTED);
                    increment('impact_assessment_action_count');
                  }}
                />
              </Tooltip>
            </ButtonGroup>
          </Stack>
          <TableContainer whiteSpace="normal">
            <Table size={'sm'}>
              <Thead sx={!isListView ? { th: { borderBottom: 'none' } } : {}}>
                <Tr>
                  {isListView ? (
                    <>
                      <Th>Relative contribution (%)</Th>
                      <Th>Contributing exchanges/intermediate</Th>
                      {hasProductColumn && <Th>Contributing exchanges/supplier</Th>}
                    </>
                  ) : (
                    <>
                      <Th>Contributing exchanges/intermediate</Th>
                      <Th>Relative contribution (%)</Th>
                    </>
                  )}
                  <Th sx={{ textAlign: isListView && hasProductColumn ? 'start' : 'end' }}>Impact ({unitName})</Th>
                </Tr>
              </Thead>
              <Tbody>
                {view === 'list' ? (
                  exchanges.map((item, key) => (
                    <DirectContributionRow
                      method={method}
                      category={category}
                      indicatorName={indicatorName}
                      contributor={item}
                      indicatorId={indicatorId}
                      index={item.index}
                      hasProductColumn={hasProductColumn}
                      key={key}
                    />
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={columnCount} p={0} border="none">
                      <DirectContributionBarChart exchanges={exchanges} impactUnit={unitName} />
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </>
  );
};
export default DirectContributionList;
